import {
    EventStatKeyColumnDescription,
    EventStatValueColumnDescription,
    KeyFilterClause,
    ReportFilter,
    ReportRequestParametersObjectObject,
} from 'common/api/api.ts'

import {CreateReportPayload, FilterKV, ReportColumnOption} from './types.ts'
import ColumnEnum = EventStatValueColumnDescription.ColumnEnum;

interface Params {
    reportType: ReportRequestParametersObjectObject.TypeEnum
    start?: string
    end?: string
    allFilters?: ReportFilter[]
    selectedFilters: FilterKV[]
    selectedKeyColumns: string[]
    selectedValueColumns: string[]
    selectedEventColumns: string[]
    reportValues: ReportColumnOption[]
    timezone?: string
}

const getDateColumn = (reportType: ReportRequestParametersObjectObject.TypeEnum) =>
    reportType === ReportRequestParametersObjectObject.TypeEnum.Tracker
        ? ('Time' as unknown as EventStatKeyColumnDescription.ColumnEnum)
        : EventStatKeyColumnDescription.ColumnEnum.Date

const collectFilterValues = (selectedFilters: FilterKV[], dimension: string): Array<string | number> => {
    const values: Array<string | number> = []

    selectedFilters.forEach((filter) => {
        if (filter.dimension !== dimension) {
            return
        }

        const raw = filter.value
        if (raw == null || raw === '') {
            return
        }

        if (Array.isArray(raw)) {
            raw.forEach((value) => {
                if (value !== null && value !== undefined && value !== '') {
                    values.push(value)
                }
            })
            return
        }

        values.push(raw)
    })

    return Array.from(new Set(values))
}

const isContainsFilter = (filter?: ReportFilter) =>
    filter?.type === ReportFilter.TypeEnum.CONTAINS

const createKeyColumns = ({
                              reportType,
                              start,
                              end,
                              allFilters,
                              selectedFilters,
                              selectedKeyColumns,
                          }: Pick<Params, 'reportType' | 'start' | 'end' | 'allFilters' | 'selectedFilters' | 'selectedKeyColumns'>) => {
    const keyColumns: Array<EventStatKeyColumnDescription> = selectedKeyColumns.map((value) => ({
        column: value as unknown as EventStatKeyColumnDescription.ColumnEnum,
    }))

    keyColumns.push({
        column: getDateColumn(reportType),
        where: {type: KeyFilterClause.TypeEnum.Between, value: [start, end] as any},
        onlyFilter: true,
    })

    const dimensions = Array.from(new Set(selectedFilters.map((filter) => filter.dimension).filter(Boolean))) as string[]
    const filtersByKey = new Map((allFilters || []).map((filter) => [filter.key, filter]))

    dimensions.forEach((dimension) => {
        const filterConfig = filtersByKey.get(dimension)
        const values = collectFilterValues(selectedFilters, dimension)
        if (values.length > 0 && isContainsFilter(filterConfig)) {
            keyColumns.push({
                column: dimension as unknown as EventStatKeyColumnDescription.ColumnEnum,
                where: {type: KeyFilterClause.TypeEnum.Contains, value: values},
                onlyFilter: true,
            })
            return
        }

        if (values.length > 0) {
            keyColumns.push({
                column: dimension as unknown as EventStatKeyColumnDescription.ColumnEnum,
                where: {type: KeyFilterClause.TypeEnum.In, value: values},
                onlyFilter: true,
            })
        }
    })

    return keyColumns
}

const createValueColumns = ({
                                reportType,
                                selectedValueColumns,
                                selectedEventColumns,
                                reportValues,
                            }: Pick<Params, 'reportType' | 'selectedValueColumns' | 'selectedEventColumns' | 'reportValues'>) => {
    if (reportType === ReportRequestParametersObjectObject.TypeEnum.Tracker) {
        return [] as Array<EventStatValueColumnDescription>
    }

    const values = selectedValueColumns.reduce<Array<EventStatValueColumnDescription>>((acc, value) => {
        const found = reportValues.find((column) => column.value === value)
        if (found?.value) {
            acc.push({column: found.value as any})
        }
        return acc
    }, [])

    if (selectedEventColumns && selectedEventColumns.length > 0) {
        selectedEventColumns.forEach(eventId => {
            const pos = eventId.indexOf('_')
            if (pos > 0) {
                values.push({
                    column: ColumnEnum.TrackerEventAmount,
                    param: eventId.substring(0, pos)
                } as EventStatValueColumnDescription)
            } else {
                values.push({column: ColumnEnum.TrackerEvent, param: eventId} as EventStatValueColumnDescription)
            }
        })
    }
    return values
}

export const buildReportPayload = ({
                                       reportType,
                                       start,
                                       end,
                                       allFilters,
                                       selectedFilters,
                                       selectedKeyColumns,
                                       selectedValueColumns,
                                       selectedEventColumns,
                                       reportValues,
                                       timezone,
                                   }: Params): CreateReportPayload => {
    const keyColumns = createKeyColumns({
        reportType,
        start,
        end,
        allFilters,
        selectedFilters,
        selectedKeyColumns,
    })
    const valueColumns = createValueColumns({
        reportType,
        selectedValueColumns,
        selectedEventColumns,
        reportValues,
    })

    return {
        keyColumns,
        valueColumns,
        type: reportType,
        timezone,
    }
}
